import React, { useEffect, useMemo, useState } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Select from "@amzn/meridian/select";
import SelectOption from "@amzn/meridian/select/select-option";
import { Icon } from "@amzn/meridian";
import chevronRightSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small";
import clipboardTokens from "@amzn/meridian-tokens/base/icon/clipboard";
import searchTokens from "@amzn/meridian-tokens/base/icon/search";
import { useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "src/store/store";
import { setActiveSiteCode } from "src/store/userSlice";
import { getSiteInspectionHistory } from "src/store/inspectionSlice";
import {
  completedInspectionStatus,
  getVisibleSites,
  hasClassificationAccess,
  hasInspectionAccess,
  isPilotInspectionTemplateName,
} from "src/constants/constants";
import { isUpdateAvailable } from "src/utils/checkForWebsiteUpdates";
import { formatTime, isUnexpiredDraft } from "src/utils/dateUtil";
import austinInspectionService, { ScheduledInspection } from "src/services/austinInspectionService";
import AustinInspectionCard from "./austinInspectionCard";
import RecentInspectionCard from "./recentInspectionCard";
import "./landing.scss";

// Recent Inspections cap: at most this many completed inspections (plus all
// unexpired in-progress drafts, which are uncapped).
const MAX_RECENT_COMPLETED = 4;

/* ── Stat Card ── */
const StatCard: React.FC<{ value: number; label: string }> = ({ value, label }) => (
  <Column className="home-card" spacingInset="400 500" spacing="200" alignmentHorizontal="start">
    <Text color="success" type="h600">
      {value}
    </Text>
    <Text type="b200" color="secondary">
      {label}
    </Text>
  </Column>
);

/* ── Workflow Card ── */
interface HomeCardProps {
  icon: object;
  title: string;
  description: string;
  onClick: () => void;
}

const HomeCard: React.FC<HomeCardProps> = ({ icon, title, description, onClick }) => (
  <div className="home-card" onClick={onClick}>
    <Row alignmentVertical="center" spacing="400" spacingInset="400" alignmentHorizontal="justify">
      <Row className=" home-workflow-icon-bg" spacingInset="400">
        <Icon tokens={icon} color="success" />
      </Row>
      <Column spacing="100" width="fill">
        <Text type="h200">{title}</Text>
        <Text type="b200" color="secondary">
          {description}
        </Text>
      </Column>
      <Icon tokens={chevronRightSmallTokens} />
    </Row>
  </div>
);

/* ── Recent Activity Item (pre-Austin Recent Inspections, non-Austin sites/stages only) ── */
interface RecentItemProps {
  icon: object;
  title: string;
  subtitle: string;
  subtitleColor?: any;
  time: string;
}

const RecentItem: React.FC<RecentItemProps> = ({ icon, title, subtitle, subtitleColor, time }) => (
  <Row alignmentVertical="top" spacingInset="400" widths={["fit", "fill", "fit"]}>
    <Row spacingInset="300" className="home-recent-icon-bg">
      <Icon tokens={icon} />
    </Row>
    <Column spacing="none">
      <Text type="b300">{title}</Text>
      <Text type="b100" color={subtitleColor}>
        {subtitle}
      </Text>
    </Column>
    <Text type="b100" color="secondary">
      {time}
    </Text>
  </Row>
);

/* ── Main Home Component ── */
interface LandingHomeProps {
  austinEnabled?: boolean;
}

const LandingHome: React.FC<LandingHomeProps> = ({ austinEnabled = true }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { activeSiteCode, ldap } = useAppSelector((state) => state.user);

  // Sites this user may see/select: all sites for corporate users, or only
  // the sites their LDAP groups grant for pilot-site users.
  const visibleSites = useMemo(() => getVisibleSites(ldap), [ldap]);

  // Feature visibility: show each workflow only to users who may use it.
  const canClassify = useMemo(() => hasClassificationAccess(ldap), [ldap]);
  const canInspect = useMemo(() => hasInspectionAccess(ldap), [ldap]);

  // Default to the first visible site; reset if the current selection is no
  // longer visible (e.g. permissions changed). Skips if a valid selection exists.
  useEffect(() => {
    if (visibleSites.length === 0) return;
    if (activeSiteCode && visibleSites.some((s) => s.code === activeSiteCode)) return;
    dispatch(setActiveSiteCode(visibleSites[0].code));
  }, [activeSiteCode, visibleSites, dispatch]);

  useEffect(() => {
    if (isUpdateAvailable()) window.location.reload();
  }, []);
  const { userInspections, completedSiteInspections, incompleteSiteInspections } = useAppSelector(
    (state) => state.inspection
  );
  const { flags } = useAppSelector((state) => state.featureFlags);

  // Recent Inspections (Austin only): site-scoped history, so the section reflects
  // the selected site rather than only the current user's own inspections. Skip
  // the fetch entirely when Austin is off or the user can't inspect, matching the
  // Workflows section's gating.
  useEffect(() => {
    if (!austinEnabled || !canInspect || !activeSiteCode) return;
    dispatch(getSiteInspectionHistory(activeSiteCode));
  }, [austinEnabled, canInspect, activeSiteCode, dispatch]);

  const [scheduledInspections, setScheduledInspections] = useState<ScheduledInspection[]>([]);
  const [scheduledLoading, setScheduledLoading] = useState<boolean>(true);
  const [scheduledError, setScheduledError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Only the Austin-enabled inspection card consumes this; skip the fetch
    // entirely when Austin is off or the user can't inspect.
    if (!austinEnabled || !canInspect || !activeSiteCode) {
      setScheduledInspections([]);
      setScheduledLoading(false);
      return;
    }

    const fetchScheduled = async () => {
      setScheduledLoading(true);
      setScheduledError(null);
      try {
        const result = await austinInspectionService.getScheduledInspections(activeSiteCode);
        if (!cancelled) setScheduledInspections(result);
      } catch (e: any) {
        if (!cancelled) setScheduledError(e?.message || "Failed to load inspections");
      } finally {
        if (!cancelled) setScheduledLoading(false);
      }
    };

    fetchScheduled();
    return () => {
      cancelled = true;
    };
  }, [austinEnabled, canInspect, activeSiteCode]);

  const visibleScheduled = scheduledInspections.filter((inspection) => !!inspection.urgency);

  // Recent Inspections: at most MAX_RECENT_COMPLETED completed plus all unexpired
  // in-progress pilot (Austin) inspections for the selected site. Both site-scoped
  // lists are already sorted newest-first in the inspection slice.
  const recentCompletedInspections = completedSiteInspections
    .filter((inspection) => isPilotInspectionTemplateName(inspection.templateName))
    .slice(0, MAX_RECENT_COMPLETED);
  // A draft with no expiresAt (or one already elapsed) isn't resumable, so it's
  // excluded rather than shown as an expired card.
  const recentInProgressInspections = incompleteSiteInspections
    .filter((inspection) => isPilotInspectionTemplateName(inspection.templateName))
    .filter((inspection) => isUnexpiredDraft(inspection.expiresAt));
  const recentInspections = [...recentInProgressInspections, ...recentCompletedInspections];

  // Pre-Austin Recent Inspections (unchanged legacy behavior): the current user's own
  // 2 most recent inspections, any status, not site-scoped. Kept as-is for beta/stages
  // where Austin is disabled.
  const legacyRecentInspections = [...userInspections]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 2);

  return (
    <Column spacing="500" spacingInset="none 500 500">
      {/* Site selector */}
      <Select
        value={activeSiteCode}
        onChange={(value: string) => dispatch(setActiveSiteCode(value))}
        placeholder="Select a site"
        width="100%"
      >
        {visibleSites.map((site) => (
          <SelectOption key={site.code} value={site.code} label={site.label} />
        ))}
      </Select>

      {/* Stats row — placeholder values for now; will be replaced with real stats values going forward. */}
      {/* will add it going further once we have concrete logic. commenting out for now*/}

      {/* <Row widths={["fill", "fill"]} spacing="400">
        <StatCard value={12} label="Inspections today" />
        <StatCard value={45} label="Products classified" />
      </Row> */}

      {/* Workflows */}
      <Column spacing="400">
        <Text type="h300">Workflows</Text>
        {canClassify && (
          <HomeCard
            icon={searchTokens}
            title="Unsellable Waste Product Classification - zluamz test"
            description="AI-based waste profile identification"
            onClick={() => navigate("/classification/scan")}
          />
        )}

        {canInspect &&
          (austinEnabled && activeSiteCode ? (
            scheduledLoading ? (
              <div className="home-card">
                <Row spacingInset="400">
                  <Text type="b200" color="secondary">Loading inspections…</Text>
                </Row>
              </div>
            ) : (
              // Nothing DUE/UPCOMING (or the fetch failed) renders no cards, keeping
              // the workflow section clean.
              !scheduledError &&
              visibleScheduled.map((inspection) => (
                <AustinInspectionCard
                  key={inspection.austinInspectionId}
                  inspection={inspection}
                  siteCode={activeSiteCode}
                />
              ))
            )
          ) : (
            <HomeCard
              icon={clipboardTokens}
              title="Cage Inspection"
              description="AI-guided visual compliance check"
              onClick={() => {
                navigate("/configurationDrivenInspections");
              }}
            />
          ))}
      </Column>

      {/* Recent Inspections: Austin (pilot) view when enabled, else the legacy view. */}
      {canInspect && austinEnabled && recentInspections.length > 0 && (
        <Column spacing="400">
          <Text type="h300">Recent</Text>
          {recentInspections.map((inspection) => (
            <RecentInspectionCard key={inspection.inspectionId} inspection={inspection} />
          ))}
        </Column>
      )}
      {canInspect && !austinEnabled && legacyRecentInspections.length > 0 && (
        <Column spacing="400">
          <Text type="h300">Recent</Text>
          <Column className="home-card" spacing="none">
            {legacyRecentInspections.map((inspection) => {
              const isCompleted = inspection.status === completedInspectionStatus;
              return (
                <RecentItem
                  key={inspection.inspectionId}
                  icon={clipboardTokens}
                  title={inspection.templateName}
                  subtitle={inspection.status}
                  subtitleColor={isCompleted ? "success" : "warning"}
                  time={formatTime(inspection.createdAt)}
                />
              );
            })}
          </Column>
        </Column>
      )}
    </Column>
  );
};

export default LandingHome;
